import classNames from 'classnames'
import React, { useState } from 'react'

import { Heading6 } from '@ui/Typography'

import { Loading } from '@global/Loading/Loading'

import { ModalMobile } from '../ModalMobile'
import { ModalTitle } from '../common/components/ModalTitle'
import { cloneButtonLarge, isButtonValid } from '../common/helpers/button'
import { CommonModalProps } from '../types'
import { useExpandableModal } from './hooks/useExpandableModal'

import styles from './ExpandableBasicModalMobile.module.scss'

export interface ExpandableModalProps extends CommonModalProps {
  renderFooter?(props: ExpandableModalProps): React.ReactNode[]
}

export const ExpandableBasicModalMobile: React.FC<ExpandableModalProps> = (props) => {
  const { isLoading = false, onClose } = props

  const [isOpen, setOpen] = useState(true)

  const handleClose = async () => {
    setOpen(false)
    await new Promise((resolve) => setTimeout(resolve, 500))
    onClose?.()
  }

  return (
    <ModalMobile onClose={handleClose}>
      {isLoading ? (
        <ModalLoading />
      ) : (
        <ModalBody {...props} isOpen={isOpen} onClose={handleClose} />
      )}
    </ModalMobile>
  )
}

type ModalBody = ExpandableModalProps & { isOpen: boolean }

const ModalBody: React.FC<ModalBody> = (props) => {
  const { children, isLoading = false, isOpen, renderFooter, onClose } = props

  const expandableModalProps = useExpandableModal(props)

  const { handleTouchStart, handleTouchMove, handleTouchEnd, handleTransitionEnd } =
    expandableModalProps
  const { isModalCompact, isModalDismissed, isDragging, modalHeight } = expandableModalProps
  const { isShadowVisible } = expandableModalProps
  const { canScrollUp, canScrollDown } = expandableModalProps
  const { scrollContainerRef, scrollContentRef, modalRef, headerRef, footerRef } =
    expandableModalProps

  return (
    <div className={styles.modal}>
      <div
        className={classNames(styles.wrapper, {
          [styles.scrollActive]: isShadowVisible,
          [styles.dragging]: isDragging,
          [styles.notDragging]: !isDragging,
          [styles.dismissed]: isModalDismissed || !isOpen,
        })}
        onTransitionEnd={handleTransitionEnd}
        ref={modalRef}
        style={
          {
            '--modal-height': `${modalHeight ? `${modalHeight}%` : 'auto'}`,
          } as React.CSSProperties
        }
      >
        <ExpandableModalHeader
          {...props}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          headerRef={headerRef}
        />
        <div
          className={classNames(styles.shadow, styles.shadowTop, {
            [styles.isScrollable]: canScrollUp,
            [styles.isInitialScrollable]: isShadowVisible,
          })}
        />
        <section
          className={classNames(styles.body, {
            [styles.isScrollable]: isShadowVisible,
          })}
          ref={scrollContainerRef}
        >
          <div className={styles.content} ref={scrollContentRef}>
            <div
              className={classNames(styles.scrollContainer, {
                [styles.isScrollable]: isModalCompact,
              })}
            >
              <Loading isLoading={isLoading} showLoadingIcon wrapperClass={styles.loading}>
                {children}
              </Loading>
            </div>
          </div>
        </section>
        <div
          className={classNames(styles.shadow, styles.shadowBottom, {
            [styles.isScrollable]: canScrollDown,
            [styles.isInitialScrollable]: isShadowVisible,
          })}
        />
        {!isLoading && (
          <ExpandableModalMobileFooter
            {...props}
            buttons={renderFooter?.({ ...props, onClose })}
            footerRef={footerRef}
          />
        )}
      </div>
    </div>
  )
}

const ModalLoading: React.FC = () => {
  return (
    <div className={styles.modal}>
      <div className={styles.wrapper}>
        <ExpandableModalHeader isLoading />
        <section className={styles.body}>
          <div className={styles.content}>
            <div className={styles.scrollContainer}>
              <Loading isLoading showLoadingIcon wrapperClass={styles.loading} />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

type ExpandableModalHeaderProps = Omit<ExpandableModalProps, 'onClose'> &
  ExpandableButtonProps & {
    headerRef?: React.MutableRefObject<HTMLDivElement | null>
  }

const ExpandableModalHeader: React.FC<ExpandableModalHeaderProps> = (props) => {
  const { title, isLoading = false, headerRef } = props
  const { onTouchStart, onTouchMove, onTouchEnd } = props

  return (
    <header
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={styles.header}
      ref={headerRef}
    >
      <div className={styles.buttonWrapper}>
        <ExpandableButton />
      </div>
      {!isLoading && title && (
        <div className={styles.titleWrapper}>
          <Heading6 className={styles.title} medium>
            <span className={styles.titleText}>
              <ModalTitle title={title} />
            </span>
          </Heading6>
        </div>
      )}
    </header>
  )
}

interface ExpandableButtonProps {
  onTouchStart?(event: React.TouchEvent): void
  onTouchMove?(event: React.TouchEvent): void
  onTouchEnd?(event: React.TouchEvent): void
}

const ExpandableButton: React.FC = () => {
  return <button type='button' className={styles.buttonExpandable} />
}

interface Props {
  buttons?: React.ReactNode[]
  footerRef: React.MutableRefObject<HTMLDivElement | null>
}

const ExpandableModalMobileFooter: React.FC<Props> = (props) => {
  const { buttons = [], footerRef } = props

  if (buttons?.length > 0) {
    return (
      <footer
        className={classNames(styles.footer, {
          [styles.compact]: buttons?.length > 2,
        })}
        ref={footerRef}
      >
        {buttons.map((button, index) => {
          return isButtonValid(button) ? (
            <span className={styles.footerButton} key={index}>
              {cloneButtonLarge(button)}
            </span>
          ) : (
            <span key={index}>{button}</span>
          )
        })}
      </footer>
    )
  }

  return null
}
